import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Loader2,
  Trash2,
  CheckCircle2,
  RotateCcw,
  Mail,
  Building2,
  Phone,
  Inbox,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { Seo } from "../components/Seo";
import { Logo } from "../components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "handled", label: "Handled" },
];

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success(status === "handled" ? "Marked as handled" : "Reopened");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Update failed");
      }
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Delete failed");
      }
    } finally {
      setBusy(null);
    }
  };

  const filtered = leads.filter((l) => filter === "all" || l.status === filter);
  const counts = {
    all: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    handled: leads.filter((l) => l.status === "handled").length,
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Leads Dashboard | Interlocked Solutions" description="Private leads dashboard." path="/admin" noindex />
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Logo className="h-9" />
            <span className="hidden font-mono-ui text-[11px] uppercase tracking-[0.2em] text-muted-foreground sm:inline">
              Leads Dashboard
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="max-w-[140px] truncate text-sm text-muted-foreground sm:max-w-none" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-foreground transition-colors hover:border-foreground"
            >
              <LogOut size={15} strokeWidth={1.5} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-medium tracking-tight lg:text-4xl">
              Demo requests
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {counts.new} new · {counts.handled} handled · {counts.all} total
            </p>
          </div>
          <div className="flex gap-2" data-testid="admin-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`admin-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                  filter === f.key
                    ? "border-primary bg-primary text-primary-foreground font-semibold"
                    : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                }`}
              >
                {f.label} ({counts[f.key]})
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="animate-spin text-primary" size={26} />
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card py-24 text-center"
          >
            <Inbox size={34} strokeWidth={1.25} className="text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">No {filter !== "all" ? filter : ""} leads yet.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-leads-list">
            <AnimatePresence initial={false}>
              {filtered.map((lead) => (
                <motion.div
                  key={lead.id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`admin-lead-${lead.id}`}
                  className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-muted-foreground/40"
                >
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-heading text-lg font-medium tracking-tight">{lead.name}</h3>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${
                            lead.status === "handled"
                              ? "bg-secondary/20 text-secondary"
                              : "bg-primary/15 text-primary"
                          }`}
                        >
                          {lead.status}
                        </span>
                        <span className="rounded-full border border-border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                          {lead.interest}
                        </span>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                        <a
                          href={`mailto:${lead.email}`}
                          className="inline-flex items-center gap-2 transition-colors hover:text-primary"
                        >
                          <Mail size={14} strokeWidth={1.5} /> {lead.email}
                        </a>
                        {lead.company ? (
                          <span className="inline-flex items-center gap-2">
                            <Building2 size={14} strokeWidth={1.5} /> {lead.company}
                          </span>
                        ) : null}
                        {lead.phone ? (
                          <span className="inline-flex items-center gap-2">
                            <Phone size={14} strokeWidth={1.5} /> {lead.phone}
                          </span>
                        ) : null}
                      </div>

                      {lead.message ? (
                        <p className="mt-4 max-w-2xl rounded-lg border border-border bg-background/40 p-3 text-sm leading-relaxed text-foreground/80">
                          {lead.message}
                        </p>
                      ) : null}

                      <p className="mt-4 font-mono-ui text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                        {fmtDate(lead.created_at)}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {lead.status === "new" ? (
                        <button
                          data-testid={`admin-handle-${lead.id}`}
                          disabled={busy === lead.id}
                          onClick={() => setStatus(lead.id, "handled")}
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50"
                        >
                          <CheckCircle2 size={14} /> Mark handled
                        </button>
                      ) : (
                        <button
                          data-testid={`admin-reopen-${lead.id}`}
                          disabled={busy === lead.id}
                          onClick={() => setStatus(lead.id, "new")}
                          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-foreground transition-colors hover:border-foreground disabled:opacity-50"
                        >
                          <RotateCcw size={14} /> Reopen
                        </button>
                      )}
                      <button
                        data-testid={`admin-delete-${lead.id}`}
                        disabled={busy === lead.id}
                        onClick={() => remove(lead.id)}
                        className="inline-flex items-center justify-center rounded-full border border-border p-2 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive disabled:opacity-50"
                        aria-label="Delete lead"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
};
